import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { ComponentProps } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { TextField } from '@mi-front/ui';

const clearMessage = defineMessage({
  id: 'common.clear',
  defaultMessage: 'Limpiar',
});

type ClearableTextFieldProps = Omit<ComponentProps<typeof TextField>, 'name'> & {
  name: string;
  submitOnClear?: boolean;
};

/**
 * Renders a Formik text field with a button that clears its current value.
 */
export function ClearableTextField({
  name,
  submitOnClear = false,
  ...props
}: ClearableTextFieldProps) {
  const { $t } = useIntl();
  const [field] = useField<string>(name);
  const { setFieldValue, submitForm } = useFormikContext();
  const hasValue = Boolean(field.value);

  const handleClear = async () => {
    await setFieldValue(name, '', false);

    if (submitOnClear) {
      await submitForm();
    }
  };

  return (
    <Box sx={{ position: 'relative', '& .form-control': { pr: 5 } }}>
      <TextField name={name} {...props} />

      {hasValue && (
        <IconButton
          type="button"
          size="small"
          title={$t(clearMessage)}
          onClick={handleClear}
          sx={{ position: 'absolute', right: 1, bottom: '0.375rem' }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
